<template>
  <div class="section">
    <OneHead 
      :brandClass="brandClass()" :isHeader="false">
      <template v-slot:title>{{$toUpperCase($t("profile_consents_summary_title"))}}</template>
      <template v-slot:subtitle>{{$t("profile_consents_summary_subtitle")}}</template>
    </OneHead>

    <hr alt="" role="presentation">
    <div style="padding-left:5px;" v-if="$c('show_profile_marketing_consents') && !consentsUpdated">
      <!--[GCDM-234858] Select-All-Checkbox-Implementation-of-a-Select-All-Checkbox-for-Korea-->
      <div class="select-all-container" :class="brandClass()" v-if="$c('show_select_all_checkboxes') && policyDocuments.length>0">
        <OneCheckbox
          :brandClass="brandClass()" 
          v-model="selectAll" 
          :paramName="'select_all'"
          :id="'select_all'"
          @input="(e)=>{selectUnselectAll(e)}"
          :required="false"
          :labelledBy="'select-all-label'"
        >
          <template v-slot:labelcontent>
            <span class="select-all-label" >
              <span :class="brandClass()">
                <span id="select-all-label">{{$t('select_all')}}</span>
              </span>
            </span>
          </template>
        </OneCheckbox>
      </div>
      <OnePolicyConsents
        v-for="(policyConfig, index) in policyDocuments"
        :ref="'policy-document-'+index"
        :policyIndex="index"
        :policyConfig="policyConfig"
        :key="policyConfig.policy_document_name"
        :isProfileSection="true"
        :brandClass="brandClass()"
        :t="$t"
        :c="$c"
        @policyDowloaded="savePolicyObject($event)"
        @policyUpdated="policyUpdated($event)"
      ></OnePolicyConsents>
      <div class="buttonsContainer">
        <OneButton
          :brandClass="brandClass()"
          :profileStyle="true"
          :type="'primary'"
          :label="$t('profile_submit_button')"
          @click="onSaveButtonClick()"
          :disabled="!arePolicyDirty"
        />
      </div>
    </div>
    <div v-else-if="consentsUpdated" class="profile-consents-saved-successfully" :class="brandClass()">
      {{$t("profile_consents_messages_saved_sucessfully")}}
    </div>
    <div v-else style="margin-top: 50px;">
      {{$t("profile_consents_messages_consents_not_available")}}
    </div>

    <template v-if="isTACConsentsListVisible && userPolicyConsentSections?.length">
      <div style="margin-bottom: 1rem">
        <hr alt="" role="presentation">
      </div>

      <OneHead 
        :brandClass="brandClass()" :isHeader="false">
        <template v-slot:title>{{$toUpperCase($t("profile_terms_and_condition_title"))}}</template>
        <template v-if="userPolicyConsentSections?.length" v-slot:subtitle>{{$t("profile_consents_terms_and_conditions_summary_subtitle")}}</template>
      </OneHead>

      <div v-for="(userPolicyConsent, consentIndex) in userPolicyConsentSections" :key="consentIndex" class="profile-consents-terms-and-conditions-accordion-element-wrapper">
        <div 
          class="profile-consents-terms-and-conditions-accordion-clickable-element"
          @click="policyConsentOpenIndex[consentIndex] = !policyConsentOpenIndex[consentIndex]"
          @keydown="ariaClick" 
          role="button" 
          tabindex="0">
          <div v-if="policyConsentOpenIndex[consentIndex]" class="profile-consents-terms-and-conditions-accordion-element-arrow profile-consents-terms-and-conditions-accordion-element-arrow-down" :class="brandClass()" />
          <div v-else class="profile-consents-terms-and-conditions-accordion-element-arrow profile-consents-terms-and-conditions-accordion-element-arrow-right" :class="brandClass()" />
          <div class="profile-consents-terms-and-conditions-accordion-element-title">
            {{$t("profile_terms_and_condition_title")}} {{ userPolicyConsent.documentNameTranslation }}
          </div>
        </div>
        <div v-if="policyConsentOpenIndex[consentIndex]" class="profile-consents-terms-and-conditions-accordion-element-description">
          {{$t("profile_consents_terms_and_conditions_accordion_subtitle_prefix")}} <a :href="getPolicyConsentLink(userPolicyConsent)" target="_blank" rel="noopener" class="profile-consents-terms-and-conditions-accordion-element-description-link">{{$t("profile_consents_terms_and_conditions_accordion_subtitle_link")}}</a> {{$t("profile_consents_terms_and_conditions_accordion_subtitle_suffix")}} {{ userPolicyConsent.documentNameTranslation }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";
import OnePolicyConsents from '../../../../gcdm-one-library/gcdm-one-policy-consents/src/OnePolicyConsents.vue';
import VueJsonPretty from "vue-json-pretty";
import { onBeforeMount } from 'vue'
import OneCheckbox from "../../../../gcdm-one-library/gcdm-one-checkbox/src/OneCheckbox";
import { useStore } from "vuex";

export default {
  setup() {
    onBeforeMount(async () => {
      const store = useStore();
      oneUtils.utils.showGlobalLoader();
      const res = await oneUtils.httpClient
        .getConsents(store.getters.getUser.gcid, window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token)
          .then((res) => {
            if (res) {
              store.dispatch("setupContactPolicyConsents", res.data);
              oneUtils.utils.hideGlobalLoader();
            } else {
              oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
              oneUtils.utils.hideGlobalLoader();
            }
          })
          .catch((err) => {
              oneUtils.utils.hideGlobalLoader();
          });
    });
  },
  inject:['$c', '$t', 'brandClass', '$toUpperCase'],
  mixins: [oneUtils.accessibilityMixin], 
  components: {
    OneCheckbox,
    OnePolicyConsents,
    VueJsonPretty
  },
  name: "ConsentsSection",
  data: () => ({
    selectAll: false,
    consentsUpdated:false,
    policyConsentsToSend: {},
    policyConsentOpenIndex: []
  }),
  computed:{
    user() {
      return this.$store.getters.getUser;
    },
    policyDocuments() {
      const policyDocumentsFromConfig = this.$c("list_of_policy_documents");
      if (!policyDocumentsFromConfig) return [];
      return policyDocumentsFromConfig.filter(
        (p) => p.policy_document_name && p.type && p.type=='mrk_type'
      ).sort((a, b) => {
        if (a.order < b.order) return -1;
        if (a.order > b.order) return 1;
        return 0;
      });
    },
    arePolicyDirty(){
      return Object.keys(this.policyConsentsToSend).find(p=>this.policyConsentsToSend[p].dirty);
    },
    TACConsentsListConfig() {
      return this.$c("_profile_tac_consents_configuration");
    },
    isTACConsentsListVisible() {
      return this.TACConsentsListConfig?.show_profile_tac_consents_list ?? false;
    },
    TACPolicyDocumentsList() {
      return this.TACConsentsListConfig?.profile_policy_documents_list ?? [];
    },
    userPolicyConsents() {
      return this.$store.getters.getUser.contactPolicyConsents;
    },
    userPolicyConsentSections() {
      // Prepare an array variable to return
      let userPolicyConsentSections = [];

      // For each TAC policy document name specified in config...
      this.TACPolicyDocumentsList.forEach(policyDocument => {

        // Filter the user accepted consents according to the TAC policy document name
        let filteredUserPolicyConsents = this.filterAcceptedUserPolicyConsentsByPolicyId(policyDocument.policy_id);

        // If there's at least one accepted consent with said TAC document name...
        if(filteredUserPolicyConsents.length) {

          // Prepare a varaible to store the most updated consent in the previous list 
          let mostRecentFilteredUserPolicyConsent = {};

          // Find the most updated consent in the previous list
          filteredUserPolicyConsents.forEach(filteredUserPolicyConsent => {
            let isMajorVersionGreater = filteredUserPolicyConsent.majorVersion > (mostRecentFilteredUserPolicyConsent.majorVersion ?? 0);
            let isMajorVersionEqual = filteredUserPolicyConsent.majorVersion == (mostRecentFilteredUserPolicyConsent.majorVersion ?? 0);
            let isMinorVersionGreater = filteredUserPolicyConsent.minorVersion > (mostRecentFilteredUserPolicyConsent.minorVersion ?? 0);

            if(
              isMajorVersionGreater || 
              (
                isMajorVersionEqual &&
                isMinorVersionGreater
              )
            ) {
              mostRecentFilteredUserPolicyConsent = filteredUserPolicyConsent;
            }
          });

          // Push it in the array variable to return
          userPolicyConsentSections.push({
            documentNameTranslation: policyDocument.document_name_translation,
            ...mostRecentFilteredUserPolicyConsent
          });
        }
      });

      return userPolicyConsentSections;
    },
  },
  watch: {
    "$store.state.configurationParams": {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal.language !== oldVal.language) {
          console.log(`[ConsentsSection] language change detected: '${oldVal.language}' -> '${newVal.language}'`);
        }
      },
    },
  },
  methods: {
    //[GCDM-234858] Select-All-Checkbox-Implementation-of-a-Select-All-Checkbox-for-Korea
    selectUnselectAll(e){
      for (let index = 0; index < this.policyDocuments.length; index++) {
        let policyKey = this.policyDocuments[index].policy_document_name;
        this.policyConsentsToSend[policyKey].consents.map((p)=>{p.consent = !this.selectAll ? 'ACCEPT' : 'REJECT'});
        this.policyConsentsToSend[policyKey].dirty=true;
        this.$refs['policy-document-'+index][0].selectAll(!this.selectAll);
      }
    }, 
    // Start valorization of the policy consents element
    savePolicyObject(policyInfo){
      this.policyConsentsToSend[policyInfo.policy_document_name] = policyInfo;
      if(this.$c('show_select_all_checkboxes')) this.handleSelectAll();
    },

    policyUpdated(policyInfo){
      this.policyConsentsToSend[policyInfo.policy_document_name].consents.map((p)=>{if(p.usage_id==policyInfo.usageId)p.consent=policyInfo.accepted});
      this.policyConsentsToSend[policyInfo.policy_document_name].dirty=true;
      if(this.$c('show_select_all_checkboxes')) this.handleSelectAll();
    },
    
    // [GCDM_238647] KR-Select-All-Checkbox, handle select all checkbox status depending on other checkboxes
    handleSelectAll(){
      for(let policyDocument in this.policyConsentsToSend) {
        if(this.policyConsentsToSend[policyDocument]['consents'].find(c => c.consent != 'ACCEPT')){
          this.selectAll = false;
          break;
        }else{
          this.selectAll = true;
        }
      }
    },

    // End valorization of the policy consents element

    /**
     * This function save the policy documents
     * and manage the errors if present
     */
    
    onSaveButtonClick(){
      let policyConsents = [];
      // get policy document value
      for (const policyDocument in this.policyConsentsToSend) {
        policyConsents = policyConsents.concat(this.policyConsentsToSend[policyDocument].consents);
      }
      const now = new Date().toISOString();
      policyConsents.map(p=>{delete p.variant; delete p.usage_id; p.consentDate=now; return p});
      oneUtils.utils.showGlobalLoader();
      oneUtils.httpClient.saveConsents(policyConsents, window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token)
      .then((data) => {
        oneUtils.utils.showMessage("SUCCESS", this.$t("profile_consents_messages_saved_sucessfully"));
        oneUtils.utils.hideGlobalLoader();
        this.consentsUpdated=true;

      //   // oneUtils.httpClient
      //     // .getUserInfo(window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token)
      //     //   .then((res) => {
      //     //     if (res) {
      //     //       oneUtils.trackingUtils.trackTracyEvent('consent changed',{});
      //     //       oneUtils.utils.showMessage("SUCCESS", this.$t("profile_consents_messages_saved_sucessfully"));
      //     //       oneUtils.utils.hideGlobalLoader();
      //     //       this.consentsUpdated=true;
      //     //     } else {
      //     //       oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
      //     //       oneUtils.utils.hideGlobalLoader();
      //     //     }
      //     //   })
      //     //   .catch((err) => {
      //     //       oneUtils.trackingUtils.trackTracyError(err.errorPayload);
      //     //       oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
      //     //       oneUtils.utils.hideGlobalLoader();
      //     //   });
      })
      .catch((err) => {
        oneUtils.utils.hideGlobalLoader();
        oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
      });
    },
    /**
     * Filters the user accepted consents according to the provided TAC policy document name
     * 
     * @param policyId The TAC policy document name to filter by
     * 
     * @returns The array of accepted consents, filtered by the provided TAC policy document name
     */
    filterAcceptedUserPolicyConsentsByPolicyId(policyId) {
      let filteredAcceptedUserPolicyConsentsByPolicyId = [];

      filteredAcceptedUserPolicyConsentsByPolicyId = this.userPolicyConsents?.filter(userPolicyConsent => 
        userPolicyConsent.consent == "ACCEPT" &&
        userPolicyConsent.country == this.$store.state.configurationParams.country &&
        userPolicyConsent.language == this.$store.state.configurationParams.language &&
        userPolicyConsent.policyId == policyId
      ) ?? [];
        
      return filteredAcceptedUserPolicyConsentsByPolicyId;
    },
    getPolicyConsentLink(consent) {
      let url = oneUtils.httpClient.prepareUrl(this.$c("api_url_terms_condition_fallback"));
      url = url.replace("${policy_document_name}", consent.policyId);
      url = url.replace(
        "${country}",
        this.$c("_terms_condition_fixed_country") ||
        this.$store.state.configurationParams.country
      );
      url = url.replace("${key_for_terms_condition_id}", this.$c("key_for_terms_condition_id"));
      url = url.replace("${language}", this.$store.state.configurationParams.language);
      url += "&majorVersion=" + consent.majorVersion;
      url += "&minorVersion=" + consent.minorVersion;
      return url;
    }
  }
};
</script>

<style lang="scss">
.select-all-label{
  font-size:16px;
  min-height: 24px;
  .__m{
    font-size: 18px;
  }
}
.select-all-container{
  display: flex;
  margin-bottom: 20px;
  .custom-field--valid{
    margin-top: 4px;
  }
}
.profile-consents-saved-successfully {
    margin-top: 50px;
    left: 0;
    right: 0;
    text-align: left;
    padding: 14px 1%;
    background: white;
    align-items: center;
    font-size: 12px;
  &.__b {
    color: $messagesuccess;
    background-color: $messagesuccessbg;
  }
  &.__m {
    color: $messagesuccessmini;
    background-color: $messagesuccessbgmini;
  }
  &.__t {
    color: $messagesuccess;
    background-color: $messagesuccessbg;
  }
  &.__alphera {
    color: $messagesuccess;
    background-color: $messagesuccessbg;
  }
  &.__alphabet {
    color: $messagesuccess;
    background-color: $messagesuccessbg;
  }
  &.__rr {
    color: $messagesuccess;
    background-color: $messagesuccessbg;
  }
}

.profile-consents-terms-and-conditions-accordion-element-wrapper {
  padding-left: 4px;
  
  .profile-consents-terms-and-conditions-accordion-clickable-element {
    display: flex;
    align-items: center;
    margin-top: 1rem;
    width: fit-content;
    cursor: pointer;

    .profile-consents-terms-and-conditions-accordion-element-arrow {
      width: 24px;
      height: 24px;
      background-image: url('~@/assets/default/light/edit-default-light.svg');
      background-repeat: no-repeat;
      background-position: center;
      background-size: 24px 24px;

      &.__d {
        background-image: url('~@/assets/default/dark/edit-default-dark.svg');
      }
    }

    .profile-consents-terms-and-conditions-accordion-element-title {
      margin-left: 0.75rem;
      font-size: 16px;
    }
  }

  .profile-consents-terms-and-conditions-accordion-element-description {
    margin-left: calc(24px + 0.75rem);
    margin-top: 0.75rem;
    font-size: 14px;

    /*.profile-consents-terms-and-conditions-accordion-element-description-link {
      color: $blue;
    } */
  }
}
</style>
